import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { markNewsVisited } from "../hooks/useNewsLastVisit";

/** Timestamp of the most recent changelog entry - used by Navigation to decide whether
 * to show the unread dot. Bump this whenever a new entry is added at the top. */
export const LATEST_NEWS_TIMESTAMP_MS = new Date("2026-09-20").getTime();

function Changelog() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Visiting the news page marks all current entries as seen. Stamp with the latest
    // entry's timestamp (not Date.now()) so a clock skew can't make new entries appear
    // pre-read.
    markNewsVisited(LATEST_NEWS_TIMESTAMP_MS);
  }, []);

  const changelogEntries = [
    {
      date: "20.9.2026 - " + (i18n.language === "cs" ? "Vlastní žebříčky a vlastní turnaje!" : "Custom ladders and custom tournaments!"),
      notes: [
        "Přihlášení uživatelé si nyní mohou vytvořit vlastní žebříček a pořádat v něm turnaje - u každého žebříčku jde nastavit, zda se má počítat Elo, výkonnostní rating a udělovat ocenění",
        "Ke svému žebříčku můžeš přidat další organizátory, kteří v něm mohou zakládat a spravovat turnaje",
        "Turnaj lze nově založit i úplně bez žebříčku - pro přátelské zápasy, které nemají ovlivnit žádné pořadí",
        "Vlastní žebříčky najdeš v novém přehledu neoficiálních žebříčků",
        "Detail turnaje (včetně výsledků a pořadí) se nyní automaticky obnovuje - už není potřeba stránku ručně refreshovat",
        "Přibyla úplně nová Nástěnka! Nakresli nebo napiš vzkaz na papírek, vyber mu barvu a připíchni ho na společnou nástěnku - najdeš ji v horním menu",
      ],
      notesEnglish: [
        "Logged-in users can now create their own ladder and run tournaments in it - each ladder lets you choose whether Elo, performance rating and achievements are calculated",
        "You can add other organizers to your ladder who can create and manage tournaments in it",
        "A tournament can now also be created with no ladder at all - for friendly matches that shouldn't affect any ranking",
        "Custom ladders can be found in the new unofficial ladders overview",
        "The tournament detail (including results and standings) now refreshes automatically - no more manual page reloading",
        "A brand new Board has arrived! Draw or write a message on a sticky note, pick its color and pin it to the shared board - you'll find it in the top menu",
      ],
    },
    {
      date: "3.7.2026 - " + (i18n.language === "cs" ? "Velký chat update!" : "Big chat update!"),
      notes: [
        "Chat je nyní v reálném čase - nové zprávy, reakce, úpravy, mazání i připnutí se objevují okamžitě bez nutnosti obnovovat stránku",
        "Přidány @zmínky - když píšeš zprávu a napíšeš @, otevře se seznam hráčů. Zmínění hráči dostanou upozornění (červený puntík v navigaci místo bílého) a jméno se ve zprávě zvýrazní",
        "Přidána možnost odpovědět na zprávu - klikni na šipku u zprávy a odpověď se automaticky zobrazí s náhledem původní zprávy",
        "Vlastní zprávy je nyní možné do 1 minuty upravit (ikona tužky u zprávy)",
        "Administrátoři mohou zprávy připnout - připnuté zprávy jsou vidět v horní části chatu",
        "Přidán 'kdo píše' indikátor - když někdo právě píše zprávu, ostatní vidí 'Franta píše…' pod inputem",
        "U reakcí (emoji) se nyní po najetí myší ukáže tooltip s jmény hráčů, kteří danou reakci dali",
        "Přidán GIF picker - vedle emoji tlačítka je nyní tlačítko GIF, které otevře prohledávatelnou galerii GIFů z GIPHY",
        "Do chatu se nyní přidává nová postava - 'Hravý Bot' bude průběžně přispívat novinkami a zajímavými událostmi z turnajů. Nechte se překvapit :)",
      ],
      notesEnglish: [
        "The chat is now real-time - new messages, reactions, edits, deletes, and pins appear instantly without needing to refresh the page",
        "Added @mentions - when writing a message and typing @, a player picker opens. Mentioned players get a notification (red dot in the navigation instead of white) and the name is highlighted in the message",
        "Added a reply feature - click the arrow on a message and your reply will automatically show a preview of the original message",
        "Your own messages can now be edited within 1 minute of sending (pencil icon on the message)",
        "Administrators can pin messages - pinned messages appear at the top of the chat",
        "Added a typing indicator - when someone is typing, others see 'Franta is typing…' below the input",
        "Reactions (emoji) now show a tooltip on hover with the names of players who reacted",
        "Added a GIF picker - a new GIF button next to the emoji button opens a searchable gallery of GIFs from GIPHY",
        "A new character joins the chat - 'Hravý Bot' will occasionally chime in with news and interesting moments from the tournaments. Stay tuned :)",
      ],
    },
    {
      date: "1.7.2026",
      notes: [
        "Chat byl přidán i do detailu turnaje - každý turnaj má nyní vlastní chat (vedle hlavního globálního). V chatu se nyní také vykreslují odkazy a obrázky jako klikatelné prvky, a každý uživatel má vlastní barvu pro snadnou identifikaci",
        "Ve zprávách lze nyní vkládat smajlíky z velké emoji nabídky",
        "Na zprávy v chatu lze reagovat emoji - rychlé reakce 👍 ❤️ 🖕 nebo libovolný emoji z nabídky",
        "V navigaci se zobrazuje červená tečka, když jsou v chatu (nebo v novinkách) nepřečtené záznamy",
        "Přidány nové achievementy 'Flash' (nejrychlejší zápas) a 'Slowpoke' (nejdelší zápas)",
        "V detailu turnaje přibyla záložka 'Vyhlášení' se slavnostním vyhlášením vítězů a fun facts",
        "V nově vygenerovaných kolech se nyní podle historických dat automaticky označí zápasy, u kterých je zvýšené riziko dlouhého průběhu (červený okraj) - organizátorům to pomůže s plánováním",
        "Drobné opravy zobrazení na mobilních zařízeních (mimo jiné záložka 'Moje zápasy' v detailu turnaje)",
      ],
      notesEnglish: [
        "Chat has been added directly to tournament details - each tournament now has its own chat (alongside the main global one). Messages now also render links and images as inline clickable elements, and each user has their own color for easy identification",
        "Messages now support inserting emoji from a large emoji picker",
        "Chat messages can now be reacted to with emoji - quick reactions 👍 ❤️ 🖕 or any emoji from the picker",
        "The navigation shows a red dot when there are unread items in chat (or in news)",
        "Added new 'Flash' (fastest match) and 'Slowpoke' (longest match) achievements",
        "A new 'Awards Ceremony' tab has been added to the tournament detail with a celebratory winner announcement and fun facts",
        "In freshly generated rounds, matches whose players have historically long-average games are now automatically flagged (red border) - this helps organizers plan the schedule",
        "Minor display fixes on mobile devices (including the 'My matches' tab in tournament details)",
      ],
    },
    {
      date: "1.3.2026",
      notes: [
        "Web má nové barevné schéma, navíc je nyní možné si vybrát mezi dvěma styly (denním a nočním)",
        "Stránky 'Turnaje' a 'Žebříčky' byly přepracovány do nové podoby",
        "U každého zápasu je nyní možné rozkliknout si jeho detail, kde jsou zobrazeny různé statistiky týkající se zápasu, hráčů, a jejich dalších vzájemných zápasů",
        "Byl přidán nový systém turnaje, který podporuje skupinovou fázi + play-off",
        "Turnaje se švýcarským systémem mají nový bodovací systém - vítězové nyní získají jeden bonusový bod (navíc k bodům za vyhrané sety)",
      ],
      notesEnglish: [
        "The website has a new color scheme, and you can now choose between two styles (light and dark mode)",
        "The 'Tournaments' and 'Ladders' pages have been redesigned",
        "You can now click on any match to see its detail, which displays various statistics about the match, the players, and their head-to-head history",
        "A new tournament system has been added that supports a group stage + play-off",
        "Swiss system tournaments have a new scoring system - winners now receive one bonus point (in addition to points for won sets)",
      ],
    },
    {
      date: "9.10.2025",
      notes: [
        "Výpočet výkonnostního ratingu byl upraven a rating byl zpětně přepočítán",
        "Přibyly nové achievementy týkající se ratingu",
        "V záložce 'O aplikaci' přibylo obecné vysvětlení výkonnostního ratingu",
        "Průměrný percentil v profilu hráče byl nahrazen průměrným výkonnostním ratingem",
      ],
      notesEnglish: [
        "The calculation of performance rating has been adjusted and the rating has been recalculated",
        "Added new rating-related achievements",
        "Added a general description of the performance rating to the 'About the app' page",
        "Average percentile in the player profile has been replaced with average performance rating",
      ],
    },
    {
      date: "3.10.2025",
      notes: [
        "Výpočet změny Elo ratingu byl mírně upraven - změna bude opět aplikována zpětně i na všechny odehrané zápasy",
        "V hráčském profilu přibyla nová metrika 'Rating', která má za cíl měřit výkon hráče v jednotlivých zápasech a turnajích. Je to další věc, kterou nejspíš budu průběžně ladit a zpětně přepočítávat (a nebo ji v budoucnu úplně odstraním, pokud se mi nebude zdát jak funguje :D)",
        "Nově přídaná metrika 'Rating' přibyla i v síni slávy, v záložce 'Top zápasy' v detailu turnaje, a navíc i jako samostatná záložka v detailu turnaje",
      ],
      notesEnglish: [
        "The calculation of Elo rating changes has been slightly adjusted – the change will again be applied retroactively to all previously played matches",
        "A new metric 'Rating' has been added to the player profile, aimed at measuring a player's performance in individual matches and tournaments. This is something I will probably keep tweaking and recalculating retroactively (or possibly remove entirely in the future if I’m not satisfied with how it works :D)",
        "The newly added 'Rating' metric has also been included in the Hall of Fame, in the 'Top Matches' tab in the tournament details, and additionally as a separate tab in the tournament details"
      ],
    },
    {
      date: "1.10.2025",
      notes: [
        "V detailu turnaje přibyla záložka 'Top zápasy', kde jsou vypsány nejzajímavější zápasy (dle metrik ze síně slávy)",
        "Ocenění na profilu jsou nyní zobrazovány kompaktněji (v případě více ocenění stejného typu jsou zobrazeny pouze jednou s počtem)",
        "Zápasy je nyní možné 'lajknout' - dejte najevo, že jste si zápas užili! :) (nebo likujte prohry Vašich kamarádů :D)",
        "Administrátoři nyní v přehledu skupin můžou k jednotlivým skupinám přidat poznámku (například pro uvedení stolů, na kterých se hraje)",
        "V detailu turnaje lze nyní filtrovat zápasy podle jmen hráčů",
        "Upravena navigace v detailu turnaje na mobilních zařízeních - záložky by nyní měli být přehlednější",
        "V detailu turnaje pro přihlášené uživatele přibyla záložka 'Moje zápasy', uvidíte přehled všech svých zápasů v turnají a průbězný vývoj umístění",
      ],
      notesEnglish: [
        "A 'Top Matches' tab has been added to the tournament detail, listing the most interesting matches (according to Hall of Fame metrics)",
        "Achievements on the profile are now displayed more compactly (in case of multiple achievements of the same type, they are shown only once with the count)",
        "It's now possible to 'like' matches - show that you enjoyed the match! :) (or like the losses of your friends :D)",
        "Administrators can now add a note to individual groups in the group overview (for example, to indicate the tables on which they are played)",
        "Matches can now be filtered by player names in the tournament detail",
        "Navigation in the tournament detail on mobile devices has been adjusted - the tabs should now be more user-friendly",
        "A 'My matches' tab has been added to the tournament detail (for logged users only), listing all your matches in the tournament and the progress of standings",
      ],
    },
    {
      date: "8.9.2025 🥳 🎉 👶",
      notes: [
        "Narodila se budoucí pingpongová šampiónka Nina!",
      ],
      notesEnglish: [
        "The future ping pong champion Nina was born!",
      ],
    },
    {
      date: "26.8.2025",
      notes: [
        "Přidána stránka 'Síň slávy'",
      ],
      notesEnglish: [
        "Added 'Hall of Fame' page",
      ],
    },
    {
      date: "15.8.2025",
      notes: [
        "V turnajích se švýcarským systémem se nyní počítá skóre novým způsobem - každý vyhraný set znamená jeden bod, prohrané sety v bodování nehrají roli",
      ],
      notesEnglish: [
        "In Swiss system tournaments, the score is now calculated in a new way - each set won counts as one point, and lost sets do not play a role in scoring",
      ],
    },
    {
      date: "13.8.2025",
      notes: [
        "Opraveno vyhodnocení pořadí na základě vzájemného zápasu ve skupinových turnajích (promiň a díky Davide! :)",
        "V přehledu skupin jsou nyní skupiny lépe zarovnané",
      ],
      notesEnglish: [
        "Fixed the evaluation of rankings based on head-to-head matches in group tournaments (sorry and thanks, David! :)",
        "The group overview is now better aligned",
      ],
    },
    {
      date: "8.8.2025",
      notes: [
        "Ze skupinových turnajů bylo odstraněno Buchholz skóre (má smysl pouze ve švýcarském systému)",
        "Do skupinových turnajů byl přidán přehled skupin",
        "Sloupec 'Výměny' ve výsledkové tabulce turnaje nyní zobrazuje výsledek odečtení prohraných výměn od vyhraných výměn",
      ],
      notesEnglish: [
        "Buchholz score has been removed from group tournaments (it only makes sense in the Swiss system)",
        "A group overview has been added to group tournaments",
        "The 'Points' column in the tournament score table now shows the result of subtracting lost points from won points",
      ],
    },
    {
      date: "1.8.2025 - " + (i18n.language === "cs" ? "Velký letní update!" : "Huge summer update!"),
      notes: [
        "Přidána podpora pro skupinový systém turnajů",
        "V případě stejného počtu bodů se nyní budou v potaz brát prioritně vzájemné zápasy, a až poté ostatní faktory. Pokud spolu všichni hráči se steným počtem bodů neodehráli vzájemné zápasy, bude nadále fungovat starý systém",
        "Uživatelské účty - hráči si nyní můžou vytvářet účty a přihlašovat se. Díky tomu mohou zadávat výsledky zápasů samostatně, aniž by museli čekat na administrátora",
        "Pro přihlášené uživatele je nyní nově dostupný chat",
        "Pro přihlášené uživatele je nyní jednodušší najít svoje zápasy a umístění v turnaji",
        "Velké změny v designu a ovládaní na mobilních zařízeních s cílem zjednodušit navigaci v aplikaci. Pokud narazíte na nějaké problémy, dejte mi prosím vědět! :)",
        "Domovská stránka je ještě o trošku hravější",
        "V detailu turnaje byla záložka \"Nasazení\" nahrazena záložkou \"Informace\". Zároveň byla odstraněna hlavička s informacemi o turnaji, které jsou nyní dostupné v této záložce",
        "Přibyla záložky \"O aplikaci\", ve které se můžete dozvědět více o aplikaci",
        "Přibyly nové typy ocenění",
        "Točící se statistiky na domovské stránce jsou nyní lépe zarovnané na střed. Linky na turnaje a žebříčky mají nyní barvu linků (děkuji Radkovi za upozornění :)",

      ],
      notesEnglish: [
        "Added support for groups tournament system",
        "In case of the same score, head-to-head matches will now be prioritized, followed by other factors. If all players with the same number of points have not played head-to-head matches, the old system will continue to function",
        "User accounts - players can now create accounts and log in. This allows them to enter match results independently without waiting for an administrator",
        "For logged-in users, chat is now available",
        "For logged-in users, it is now easier to find their matches and standings in the tournament",
        "Major changes in design and usability on mobile devices to simplify navigation in the app. If you encounter any issues, please let me know! :)",
        "The homepage is even more playful",
        "In the tournament detail, the 'Seeding' tab has been replaced with the 'Information' tab. The header with tournament information has also been removed, as it is now available in this tab",
        "Added an 'About the app' tab, where you can learn more about the application",
        "Added new types of achievements",
        "The spinning statistics on the homepage are now better centered. The links to tournaments and rankings now have link colors (thanks to Radek for the heads-up :)",
      ],
    },
    {
      date: "8. 6. 2025",
      notes: [
        "Rozdíl Elo ratingu hráčů nyní hraje větší roli při výpočtu jeho změny. Změna je opět aplikována retroaktivně",
        "Maximální ztráta Elo ratingu při prohře s novým hráčem je nyní omezena na 16",
        "Nový vzhled domovské stránky",
      ],
      notesEnglish: [
        "The Elo rating difference between players now plays a greater role in the calculation of rating changes. The change is once again applied retroactively",
        "The maximum Elo rating loss when losing to a new player is now limited to 16",
        "New home page design",
      ],
    },
    {
      date: "6. 6. 2025",
      notes: [
        "Odstraněny všechny prvky rádoby \"inteligentního\" výpočtu Elo ratingu (včetně kalibrační fáze) - od teď hraje roli pouze Elo hráčů a vyhrané sety. Změna je opět aplikována retroaktivně",
        "Opravena chyba způsobující opakované přidělení volného losu (omlouvám se všem ovlivněným, obzvláště pak Vláďovi Veselému :D)",
        "Počet vyhraných zápasů má nyní vyšší váhu než Buchholz skóre (v případě stejného počtu bodů). V budoucnu budou nejvyšší prioritu mít vzájemné zápasy - zatím to tak ale není"
      ],
      notesEnglish: [
        "Removed all elements of the so-called 'intelligent' Elo rating calculation (including the calibration phase) – from now on, only the players' Elo and the sets won matter. The change has been applied retroactively again",
        "Fixed a bug that caused a free win (bye) to be assigned repeatedly (apologies to everyone affected, especially to Vláďa Veselý :D)",
        "The number of matches won now carries more weight than the Buchholz score (in case of the same number of points). In the future, head-to-head matches will have the highest priority – but that's not the case yet"
      ],
    },
    {
      date: "22. 5. 2025",
      notes: [
        "Vytvořeny rozdílné žebříčky pro začátečnické, mírně pokročilé a pokročilé turnaje",
        "Způsob výpočtu Elo ratingu se znovu změnil a byl retroaktivně aplikován. Je možné, že se logika bude i nadále měnit, dokud nebudeme s výsledkem spokojení",
        "V hráčském profilu přibyl barevný indikátor prohraných/vyhraných zápasů",
        "Vylepšen zápasový algoritmus tak, aby se minimalizovala šance na opakované zápasy",
      ],
      notesEnglish: [
        "Separate rankings created for beginner, intermediate, and advanced tournaments",
        "The Elo calculation method has been changed again and was retroactively applied. It's possible that the logic will continue to change until we are satisfied with the result",
        "A colored indicator for lost/won matches has been added to the player profile",
        "The match-making algorithm has been improved to minimize the chance of repeated matches",
      ],
    },
    {
      date: "8. 5. 2025",
      notes: [
        "Opravena chyba ve výpočtu Buchholz skóre a řazení hráčů (výsledky budou zpětně opraveny)",
        "Přidána vysvětlivka Buchholz skóre",
        "Akce \"zpět\" a \"vpřed\" nyní správně ovládají navigaci mezi navštívenými stránkami",
        "Datumy se nyní zobrazují v klasickém českém formátu",
        "Na profil hráče se nyní dá prokliknout ze všech tabulek",
        "Změna Elo ratingu za zápas nyní bere v potaz uhrané sety",
        "Navýšeno množství Elo ratingu, o který se v zápase hraje (bude potřeba chvíli experimentovat, mějte strpení :D)",
        "Na úvodní stránku byl přídán link do rezervačního systému",
        "K seznamu všech hráčů byl přidán vyhledávací filtr",
        "Byla vytvořena sekce \"Novinky\"",
      ],
      notesEnglish: [
        "Fixed a bug in Buchholz score calculation and player sorting (past results will be corrected)",
        "Added an explanation for the Buchholz score",
        "The \"back\" and \"forward\" actions now correctly navigate between visited pages",
        "Dates are now displayed in the traditional Czech format",
        "You can now click through to a player's profile from all tables",
        "Elo rating changes per match now take played sets into account",
        "Increased the Elo rating stakes per match (we'll need to experiment a bit - thanks for your patience! :D)",
        "Added a link to the booking system on the homepage",
        "Added a search filter to the list of all players",
        "Created a \"News\" section",
      ],
    },
    {
      date: "25. 4. 2025",
      notes: [
        "Byl uspořádán první turnaj prostřednictvím tohoto webu",
      ],
      notesEnglish: [
        "The first tournament was organized through this website",
      ],
    },
  ];

  return (
    <div className="base-container">
      <div className="table-header header">
        <span className="table-title">{t("news")}</span>
      </div>
      <div className="p-4 max-w-3xl mx-auto">
        {changelogEntries.map((entry, index) => (
          <div key={"changelog-" + index} className="mb-6">
            <h4 className="text-xl font-semibold mb-2" style={{color: "lightgreen"}}>{entry.date}</h4>
            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
              {(i18n.language === "cs" ? entry.notes : entry.notesEnglish).map((note, i) => (
                <li key={"note - " + index + " - " + i} style={{ marginBottom: "0.25rem" }}>{t(note)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Changelog;